import React, { useContext } from "react";
import { GreenMachineContext } from "../GreenMachineContext";
import "./ChevronBar.css";

// The wizard's nine internal stages collapse into six user-facing steps. The
// Sport/Contest Type/Slate/Session setup screens are represented by a single
// "Slate" node; clicking it returns to the start of that sub-flow.
export default function ChevronBar() {
  const {
    currentStage,
    STAGE_NUMBERS,
    greenMachineSessionID,
    existingLineups,
    requestNavigation,
    isNavigating,
    activeSlatesExist,
  } = useContext(GreenMachineContext);

  // Hidden on the standalone error-reports view and the no-slates empty state.
  if (currentStage === STAGE_NUMBERS.errorReports) {
    return null;
  }
  if (currentStage === STAGE_NUMBERS.selectSport && !activeSlatesExist) {
    return null;
  }

  const sessionReady = greenMachineSessionID !== undefined;

  const steps = [
    {
      key: "slate",
      label: "Slate",
      target: STAGE_NUMBERS.selectSport,
      group: [
        STAGE_NUMBERS.selectSport,
        STAGE_NUMBERS.selectGameType,
        STAGE_NUMBERS.selectSlate,
        STAGE_NUMBERS.selectSession,
      ],
      enabled: true,
    },
    {
      key: "configure",
      label: "Settings",
      target: STAGE_NUMBERS.lineupConfiguration,
      group: [STAGE_NUMBERS.lineupConfiguration],
      enabled: sessionReady,
    },
    {
      key: "players",
      label: "Players",
      target: STAGE_NUMBERS.selectPlayers,
      group: [STAGE_NUMBERS.selectPlayers],
      enabled: sessionReady,
    },
    {
      key: "rules",
      label: "Stacks",
      target: STAGE_NUMBERS.configureRules,
      group: [STAGE_NUMBERS.configureRules],
      enabled: sessionReady,
    },
    {
      key: "exposures",
      label: "Exposures",
      target: STAGE_NUMBERS.selectExposures,
      group: [STAGE_NUMBERS.selectExposures],
      enabled: sessionReady,
    },
    {
      key: "lineups",
      label: "Lineups",
      target: STAGE_NUMBERS.viewLineups,
      group: [STAGE_NUMBERS.viewLineups],
      enabled: sessionReady && existingLineups,
    },
  ];

  const activeIndex = steps.findIndex((step) => step.group.includes(currentStage));

  function stateFor(step, index) {
    if (index === activeIndex) return "active";
    if (!step.enabled) return "locked";
    if (index < activeIndex) return "done";
    return "click";
  }

  function handleClick(step, state) {
    if (isNavigating) return;
    if (state === "active" || state === "locked") return;
    // The Slate node is the start of the flow — a full page refresh is the
    // cleanest reset rather than jumping state back through the sub-flow.
    if (step.key === "slate") {
      window.location.reload();
      return;
    }
    requestNavigation(step.target);
  }

  function badge(step, index, state) {
    if (state === "done") return "✓"; // check
    if (state === "locked") return "\u{1F512}"; // lock
    return index + 1;
  }

  const activeStep = activeIndex >= 0 ? steps[activeIndex] : steps[0];

  return (
    <nav className="gm-chevron-nav" aria-label="Build progress">
      {/* Desktop / tablet: labeled notched segments */}
      <ol className="gm-chevron-bar">
        {steps.map((step, index) => {
          const state = stateFor(step, index);
          return (
            <li className="gm-chevron-item" key={step.key}>
              <button
                type="button"
                className={`gm-chevron-node gm-chevron-node--${state}`}
                aria-current={state === "active" ? "step" : undefined}
                aria-disabled={state === "locked" ? "true" : undefined}
                disabled={state === "locked" || isNavigating}
                onClick={() => handleClick(step, state)}
              >
                <span className="gm-chevron-badge">{badge(step, index, state)}</span>
                <span className="gm-chevron-label">{step.label}</span>
              </button>
            </li>
          );
        })}
      </ol>

      {/* Mobile: compact numbered dots + current-step caption */}
      <div className="gm-chevron-mobile">
        <ol className="gm-chevron-dots">
          {steps.map((step, index) => {
            const state = stateFor(step, index);
            return (
              <li
                className={`gm-chevron-dot-item gm-chevron-dot-item--${state}`}
                key={step.key}
              >
                <button
                  type="button"
                  className="gm-chevron-dot"
                  aria-label={`${step.label} (step ${index + 1} of ${steps.length})`}
                  aria-current={state === "active" ? "step" : undefined}
                  disabled={state === "locked" || isNavigating}
                  onClick={() => handleClick(step, state)}
                >
                  {badge(step, index, state)}
                </button>
              </li>
            );
          })}
        </ol>
        <div className="gm-chevron-caption">
          <span className="gm-chevron-stepof">
            Step {Math.max(activeIndex, 0) + 1} of {steps.length}
          </span>
          <span className="gm-chevron-stepname">{activeStep.label}</span>
        </div>
      </div>
    </nav>
  );
}
